/**
 * Soft heuristic that checks whether an uploaded CSV's headers resemble the
 * expected export from the platform the user selected. This never hard-blocks
 * the import; it only powers a warning so studios notice when they uploaded the
 * wrong file (e.g. selected JackRabbit but exported from another system).
 */

/** Below this fraction of expected headers present, we flag a likely mismatch. */
export const PLATFORM_MATCH_THRESHOLD = 0.4;

export interface PlatformMatchResult {
  /** True when we can judge and the file looks like it is from another system. */
  isLikelyMismatch: boolean;
  /** Fraction (0..1) of expected headers found. Undefined when not scorable. */
  score?: number;
  matchedHeaders: Array<string>;
  missingHeaders: Array<string>;
}

/** Case-insensitive; collapses spaces/punctuation so StartTime ≡ Start Time. */
function normalizeHeader(header: string): string {
  return header.trim().toLowerCase().replace(/[^a-z0-9]+/g, '');
}

export function scoreCsvAgainstPlatform(
  uploadedHeaders: Array<string>,
  expectedHeaders: Array<string>,
): PlatformMatchResult {
  // No reference headers for this platform — cannot judge.
  if (expectedHeaders.length === 0) {
    return { isLikelyMismatch: false, matchedHeaders: [], missingHeaders: [] };
  }

  const uploadedSet = new Set(uploadedHeaders.map(normalizeHeader));
  const matchedHeaders: Array<string> = [];
  const missingHeaders: Array<string> = [];

  for (const expected of expectedHeaders) {
    if (uploadedSet.has(normalizeHeader(expected))) {
      matchedHeaders.push(expected);
    } else {
      missingHeaders.push(expected);
    }
  }

  const score = matchedHeaders.length / expectedHeaders.length;

  return {
    isLikelyMismatch: score < PLATFORM_MATCH_THRESHOLD,
    score,
    matchedHeaders,
    missingHeaders,
  };
}
